import React, { useState, useEffect } from 'react';
import { Bot, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import ChatInterface from '../../components/ChatInterface';

interface Assistant {
  id: string;
  nombre: string;
  descripcion: string;
  modelo: string;
  avatar_url: string;
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);

  useEffect(() => {
    if (user) {
      fetchAssistants();
    }
  }, [user]);

  const fetchAssistants = async () => {
    try {
      const { data: assistantsData, error } = await supabase
        .from('asistentes')
        .select(`
          id,
          nombre,
          descripcion,
          modelo,
          avatar_url
        `)
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setAssistants(assistantsData || []);
    } catch (error) {
      console.error('Error fetching assistants:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartChat = (assistant: Assistant) => {
    setSelectedAssistant(assistant);
    setShowChat(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Asistentes Disponibles</h1>
        <button
          onClick={() => setShowChat(true)}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nueva Conversación
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {assistants.map((assistant) => (
          <div
            key={assistant.id}
            className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
          >
            <div className="relative">
              <img
                src={assistant.avatar_url || 'https://images.unsplash.com/photo-1464582883107-8adf2dca8a9f?w=400&auto=format&fit=crop&q=80'}
                alt={assistant.nombre}
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">{assistant.nombre}</h3>
              <p className="text-sm text-gray-500 mb-3">{assistant.modelo}</p>
              <p className="text-gray-600 text-sm line-clamp-3">{assistant.descripcion}</p>
              <button
                onClick={() => handleStartChat(assistant)}
                className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Bot className="w-5 h-5 mr-2" />
                Iniciar Chat
              </button>
            </div>
          </div>
        ))}

        {assistants.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Bot className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No hay asistentes disponibles</p>
          </div>
        )}
      </div>

      {/* Chat Interface Modal */}
      {showChat && (
        <ChatInterface 
          onClose={() => {
            setShowChat(false);
            setSelectedAssistant(null);
          }}
          selectedAssistant={selectedAssistant}
        />
      )}
    </div>
  );
}